// Upgrading @atlaskit/platform; fix these violations.
// eslint-disable-next-line @compiled/jsx-pragma
/** @jsxImportSource @compiled/react */
import * as React from 'react';

import { css } from '@compiled/react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
import styled from 'styled-components';
import { FakeButton } from '@helpcenter/fake-button';
import * as fonts from '@helpcenter/styles/src/fonts';
import * as grid from '@helpcenter/styles/src/grid';
import type { WrappedComponentProps } from 'react-intl';
import { injectIntl } from 'react-intl';
import SmoothCollapse from 'react-smooth-collapse';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { WidthObserver } from '@atlaskit/width-detector';
import { getElementHeight } from '@atlassian/help-center-common-util/dom';
import { UseTypographyMigratedComponent } from '@atlassian/help-center-common-util/enable-typography-migration';
import messages from './messages';

interface FieldDescriptionProps extends WrappedComponentProps {
    children: string | React.ReactNode;
    id: string;
}

interface FieldDescriptionState {
    shouldCollapse: boolean;
    isOpen: boolean;
}

/**
 * Will collapse a description if over two lines and show a "show more/less" button.
 */
export class FieldDescription extends React.PureComponent<FieldDescriptionProps, FieldDescriptionState> {
    state: FieldDescriptionState = {
        shouldCollapse: false,
        isOpen: false,
    };

    helpTextRef = React.createRef<HTMLElement>();

    componentDidMount() {
        this.checkIfShouldCollapse();
    }

    componentDidUpdate(prevProps: FieldDescriptionProps) {
        if (prevProps.children !== this.props.children) {
            this.checkIfShouldCollapse();
        }
    }

    checkIfShouldCollapse = () => {
        if (this.helpTextRef.current && getElementHeight(this.helpTextRef.current) > minDescriptionHeight) {
            this.setState({
                shouldCollapse: true,
            });
        } else if (this.state.shouldCollapse) {
            this.setState({
                shouldCollapse: false,
            });
        }
    };

    onToggle = (e: React.SyntheticEvent<HTMLDivElement>) => {
        e.preventDefault();
        this.setState((prevState) => ({
            isOpen: !prevState.isOpen,
        }));
    };

    renderHelpText = () => {
        const { children, id } = this.props;
        if (typeof children === 'string') {
            return (
                <UseTypographyMigratedComponent
                    migratedComponent={
                        <HelpTextWithToken
                            id={id}
                            innerRef={this.helpTextRef}
                            dangerouslySetInnerHTML={{ __html: children }}
                        />
                    }
                >
                    <HelpText id={id} innerRef={this.helpTextRef} dangerouslySetInnerHTML={{ __html: children }} />
                </UseTypographyMigratedComponent>
            );
        }
        return (
            <UseTypographyMigratedComponent
                migratedComponent={
                    <HelpTextWithToken id={id} innerRef={this.helpTextRef}>
                        {children}
                    </HelpTextWithToken>
                }
            >
                <HelpText id={id} innerRef={this.helpTextRef}>
                    {children}
                </HelpText>
            </UseTypographyMigratedComponent>
        );
    };

    render() {
        const { intl } = this.props;
        const { shouldCollapse, isOpen } = this.state;
        const removeWidthObserver = FeatureGates.checkGate('remove_widthobserver_from_field_description');
        return (
            <>
                {removeWidthObserver ? null : <WidthObserver setWidth={this.checkIfShouldCollapse} />}
                {shouldCollapse ? (
                    <StyledFakeButton onClick={this.onToggle}>
                        <SmoothCollapse expanded={isOpen} collapsedHeight={`${minDescriptionHeight}px`}>
                            {this.renderHelpText()}
                        </SmoothCollapse>

                        <UseTypographyMigratedComponent
                            migratedComponent={
                                <div css={buttonTextCSS}>
                                    <Text size="small" color="color.text.subtlest" weight="medium">
                                        {intl.formatMessage(isOpen ? messages.showLess : messages.showMore)}
                                    </Text>
                                </div>
                            }
                        >
                            <ButtonText>
                                {intl.formatMessage(isOpen ? messages.showLess : messages.showMore)}
                            </ButtonText>
                        </UseTypographyMigratedComponent>
                    </StyledFakeButton>
                ) : (
                    this.renderHelpText()
                )}
            </>
        );
    }
}

export default injectIntl(FieldDescription);

// eslint-disable-next-line rulesdir/no-styled-export, @atlaskit/ui-styling-standard/no-exported-styles, @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
export const HelpText = styled.div`
    ${fonts.xsmall};
    margin-top: ${token('space.050', '4px')};
    color: ${token('color.text.subtlest', '#6B778C')};
`;

// eslint-disable-next-line rulesdir/no-styled-export, @atlaskit/ui-styling-standard/no-exported-styles, @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
export const HelpTextWithToken = styled.div`
    font: ${token(
        'font.heading.xsmall',
        'normal 600 0.875rem/1rem ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", Ubuntu, "Helvetica Neue", sans-serif'
    )};
    margin-top: ${token('space.050', '4px')};
    color: ${token('color.text.subtlest', '#6B778C')};
`;

// eslint-disable-next-line rulesdir/no-styled-export, @atlaskit/ui-styling-standard/no-exported-styles, @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
export const ButtonText = styled.span`
    ${fonts.xsmall};
    color: ${token('color.text.subtlest', '#6B778C')};
    font-weight: 500;
`;

const buttonTextCSS = css({
    color: token('color.text.subtlest', '#626F86'),
});

// eslint-disable-next-line rulesdir/no-styled-export, @atlaskit/ui-styling-standard/no-exported-styles, @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
export const StyledFakeButton = styled(FakeButton)`
    display: block;
`;

export const minDescriptionHeight = grid.multiple(4.5).unitless;
